/* The bell: everything that needs attention, in one place inside Blueprint.
 *
 * What it lists is built in lib/attention.ts from the registers the app already holds. What has
 * been dismissed is shared (settings list), so clearing an item clears it for everyone. The email
 * digest carries on separately; this is the same watching, without leaving the browser.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAttention } from '../hooks/useAttention';
import type { AttentionGroup, AttentionItem } from '../lib/attention';
import { useToast } from './ui';
import s from './AppShell.module.scss';

const GROUP_ORDER: AttentionGroup[] = ['Sync', 'Devices', 'People', 'Expiries'];

export function AttentionBell(): JSX.Element {
  const { items, loading, dismiss } = useAttention();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  /* Closed by clicking anywhere else, like the account menu. */
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  /* A toast when something NEW turns up while the app is open (after a sync, a refresh, or somebody
     else's change arriving). The first load is the baseline and says nothing: opening the app to
     fourteen standing items should not throw fourteen toasts at you. */
  const seen = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (loading) return;
    const now = new Set(items.map((i) => i.id));
    if (seen.current === null) {
      seen.current = now;
      return;
    }
    const fresh = items.filter((i) => !seen.current!.has(i.id));
    seen.current = now;
    if (fresh.length === 1) toast(fresh[0].title, 'warn');
    else if (fresh.length > 1) toast(`${fresh.length} new things need attention`, 'warn');
  }, [items, loading, toast]);

  const grouped = useMemo(() => {
    const by = new Map<AttentionGroup, AttentionItem[]>();
    for (const i of items) by.set(i.group, [...(by.get(i.group) ?? []), i]);
    return GROUP_ORDER.filter((g) => by.has(g)).map((g) => ({ group: g, items: by.get(g)! }));
  }, [items]);

  const worst = items.some((i) => i.severity === 'bad') ? 'bad' : 'warn';

  return (
    <div className={s.bell} ref={box} data-print="hide">
      <button
        type="button"
        className={s.themeBtn}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={items.length > 0 ? `${items.length} things need attention` : 'Nothing needs attention'}
        title="Needs attention"
      >
        <i className={`ti ${items.length > 0 ? 'ti-bell-ringing' : 'ti-bell'}`} aria-hidden />
        {items.length > 0 && (
          <span className={`${s.bellCount} ${worst === 'bad' ? s.bellCountBad : ''}`}>
            {items.length > 99 ? '99+' : items.length}
          </span>
        )}
      </button>

      {open && (
        <div className={s.bellPanel} role="dialog" aria-label="Needs attention">
          <div className={s.bellHead}>
            <b>Needs attention</b>
            {items.length > 0 && (
              <button type="button" className={s.bellLink} onClick={() => dismiss(items.map((i) => i.id))}>
                Dismiss all
              </button>
            )}
          </div>

          {items.length === 0 ? (
            <div className={s.bellEmpty}>
              <i className="ti ti-circle-check" aria-hidden />
              <span>Nothing needs attention.</span>
            </div>
          ) : (
            <div className={s.bellList}>
              {grouped.map(({ group, items: rows }) => (
                <div key={group}>
                  <div className={s.bellGroup}>{group}</div>
                  {rows.map((i) => (
                    <div key={i.id} className={s.bellItem}>
                      <Link to={i.to} className={s.bellItemMain} onClick={() => setOpen(false)}>
                        <i
                          className={`ti ${i.icon} ${i.severity === 'bad' ? s.bellBad : i.severity === 'warn' ? s.bellWarn : s.bellInfo}`}
                          aria-hidden
                        />
                        <span>
                          <b>{i.title}</b>
                          <small>{i.detail}</small>
                        </span>
                      </Link>
                      <button
                        type="button"
                        className={s.bellDismiss}
                        onClick={() => dismiss([i.id])}
                        aria-label={`Dismiss: ${i.title}`}
                        title="Dismiss for everybody"
                      >
                        <i className="ti ti-x" aria-hidden />
                      </button>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          <div className={s.bellFoot}>
            <Link to="/notifications" onClick={() => setOpen(false)}>Email digest settings</Link>
            <Link to="/audit" onClick={() => setOpen(false)}>Audit trail</Link>
          </div>
        </div>
      )}
    </div>
  );
}
